import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { MessageCircle, Syringe, CheckCircle2, Loader2, ExternalLink } from "lucide-react";
import { openWhatsApp } from "../lib/whatsapp";
import { CLINIC, doctorDisplay } from "../lib/clinic";
import { RECALL_TONES, getRecallTone, setRecallTone, buildVaccineRecallMsg } from "../lib/recallTones";
import { toast } from "sonner";

/**
 * Bulk Nudge — Dashboard panel that lists all doctor-scheduled vaccines
 * coming due within a short window (default 7 days) and lets the doctor
 * fire off a one-tap WhatsApp recall to each patient. Rows already sent
 * are dimmed and marked with a check.
 */
export default function VaccineRecallsPanel({ testid = "vaccine-recalls-panel", onOpenPatient }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [window_days, setWindow] = useState(7);
  const [sending, setSending] = useState({});
  const [tone, setTone] = useState(getRecallTone());
  const pickTone = (t) => { setTone(t); setRecallTone(t); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reminders/vaccines-scheduled?window_days=${window_days}`);
      setRows(r.data.rows || []);
      setCount(r.data.count || 0);
    } catch (e) {
      // Silent — dashboard should never toast on a background fetch.
      console.debug("[vaccine-recalls]", e);
    } finally {
      setLoading(false);
    }
  }, [window_days]);

  useEffect(() => { load(); }, [load]);

  const sendOne = async (row) => {
    if (!row.patient_mobile) {
      toast.error(`${row.patient_name || "Patient"} has no mobile on file`);
      return;
    }
    const key = `${row.patient_id}__${row.vaccine}__${row.date}`;
    setSending((s) => ({ ...s, [key]: true }));
    const dt = new Date(row.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const msg = buildVaccineRecallMsg(tone, {
      name: row.patient_name || "Patient",
      vaccine: row.vaccine,
      dateStr: dt,
      daysUntil: row.days_until,
    });
    openWhatsApp(row.patient_mobile, msg);
    try {
      await api.post("/vaccine-recalls/track", {
        patient_id: row.patient_id,
        visit_id: row.visit_id,
        vaccine: row.vaccine,
        date: row.date,
      });
      // Optimistic: mark as sent locally
      setRows((prev) => prev.map((r) =>
        (r.patient_id === row.patient_id && r.vaccine === row.vaccine && r.date === row.date)
          ? { ...r, sent_at: new Date().toISOString() } : r
      ));
    } catch {
      /* swallow — tracking must not disrupt UX */
    } finally {
      setSending((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Syringe size={16} className="text-pink-600" />
          <div className="font-heading text-sm font-semibold text-slate-900">Vaccine recalls</div>
          <span className="text-[10px] font-semibold uppercase tracking-wider bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded" data-testid="vaccine-recalls-count">
            {count} due
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[10px]" data-testid="recall-tone-toggle">
            {RECALL_TONES.map((t) => (
              <button
                key={t.key}
                onClick={() => pickTone(t.key)}
                title={t.hint}
                className={`px-2 py-0.5 ${tone === t.key ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid={`recall-tone-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
            {[3, 7, 14].map((n) => (
              <button
                key={n}
                onClick={() => setWindow(n)}
                className={`px-2 py-0.5 ${window_days === n ? "bg-pink-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid={`vaccine-recalls-window-${n}`}
              >
                {n}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[11px] text-slate-500 py-3"><Loader2 size={12} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[11px] text-slate-500 py-3" data-testid="vaccine-recalls-empty">
          No vaccines scheduled in the next {window_days} days.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {rows.map((row, i) => {
            const key = `${row.patient_id}__${row.vaccine}__${row.date}`;
            const busy = !!sending[key];
            const sent = !!row.sent_at;
            const dt = new Date(row.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
            const inDays = row.days_until;
            return (
              <div
                key={key}
                className={`flex items-center gap-2 border rounded-md px-2 py-1.5 text-[11px] ${sent ? "border-slate-100 bg-slate-50/50 opacity-70" : "border-slate-200 bg-white hover:border-pink-200"}`}
                data-testid={`vaccine-recall-row-${i}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onOpenPatient && onOpenPatient(row.patient_id)}
                      className="font-medium text-slate-800 truncate hover:underline hover:text-pink-700 inline-flex items-center gap-1"
                      title="Open patient record"
                      data-testid={`vaccine-recall-open-${i}`}
                    >
                      {row.patient_name || row.patient_id}
                      <ExternalLink size={10} className="opacity-60" />
                    </button>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-700 truncate">{row.vaccine}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 tabular">
                    {dt} · {inDays === 0 ? "today" : inDays === 1 ? "tomorrow" : `in ${inDays} days`}
                    {row.patient_mobile ? ` · ${row.patient_mobile}` : " · no mobile"}
                  </div>
                </div>
                {sent ? (
                  <span className="text-emerald-700 inline-flex items-center gap-1 text-[10px]" data-testid={`vaccine-recall-sent-${i}`}>
                    <CheckCircle2 size={12} /> Sent
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => sendOne(row)}
                    disabled={busy || !row.patient_mobile}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed text-[10px]"
                    data-testid={`vaccine-recall-send-${i}`}
                    title={row.patient_mobile ? "Send WhatsApp reminder" : "No mobile on file"}
                  >
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <MessageCircle size={11} />}
                    WhatsApp
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-2">
        Tap WhatsApp to send a personalised reminder — the send is logged for Recall Analytics.
      </div>
    </div>
  );
}
